import {
  Box,
  IconButtonProps,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import React, { HTMLAttributes, useMemo } from 'react';

import { TableSkeleton } from '../_components_';

export interface ITableColumn<T> {
  id?: string;
  label: string | React.ReactNode;
  headerCellStyle?: SxProps<Theme>;
  bodyCellStyle?:
    | SxProps<Theme>
    | {
        [key: string]: any;
      }
    | ((item: T, isChildRow?: boolean) => SxProps<Theme> | { [key: string]: any });
  onHeaderCellClick?: (item: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => void;
  onBodyCellClick?: (item: T) => void;
  onBodyCellDoubleClick?: (item: T) => void;
  onCellContextMenu?: (event: React.MouseEvent<HTMLTableCellElement, MouseEvent>, item: T, columnId?: string) => void;
  render: (item: T, isChildRow?: boolean) => React.ReactNode;
}

interface ITableProps<T> {
  data?: T[] | null;
  columns: ITableColumn<T>[];
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  isLoading: boolean;
  perPageOptions?: (number | { value: number; label: string })[];
  containerStyle?: SxProps<Theme>;
  maxHeight?: string | number;
  emptyPlaceholderTitle?: string;
  emptyPlaceholderSubtitle?: string;
  emptyPlaceholderComponent?: React.ReactNode;
  emptyPlaceholderVariant?: 'block' | 'inline';
  menuAnchorEl?: HTMLElement | null;
  handleMenuClose?: () => void;
  activeRow?: T | null;
  emptyPlaceholderSpanProps?: HTMLAttributes<HTMLSpanElement>;
  getChildRows?: (item: T) => T[] | undefined;
  isRowExpandable?: (item: T) => boolean;
  expandedRows?: Set<string | number>;
  onToggleRow?: (item: T, rowIndex: number) => void;
  getRowId?: (item: T) => string | number;
}

export const TableMUI = <T, B>({
  data,
  columns,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  isLoading,
  perPageOptions,
  containerStyle,
  maxHeight = 440,
  emptyPlaceholderTitle = 'No items to show',
  emptyPlaceholderSubtitle = 'Nothing to display at the moment',
  emptyPlaceholderComponent,
  emptyPlaceholderVariant = 'block',
  menuAnchorEl,
  handleMenuClose,
  activeRow,
  emptyPlaceholderSpanProps,
  getChildRows,
  isRowExpandable,
  expandedRows,
  getRowId,
}: ITableProps<T>) => {
  const { typography, palette } = useTheme();

  const placeholder = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 400,
        }}
      >
        {emptyPlaceholderComponent ? (
          emptyPlaceholderComponent
        ) : (
          <span {...emptyPlaceholderSpanProps}>
            <Typography fontWeight={typography.fontWeightMedium}>{emptyPlaceholderTitle}</Typography>
            <Typography sx={{ fontSize: 14 }}>{emptyPlaceholderSubtitle}</Typography>
          </span>
        )}
      </Box>
    ),
    [emptyPlaceholderSubtitle, emptyPlaceholderTitle, typography.fontWeightMedium],
  );

  const isEmpty = !data || data.length === 0;
  const isInlineEmpty = isEmpty && emptyPlaceholderVariant === 'inline';
  const rows = data ?? [];

  const onContextMenu = React.useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, item: T, column: ITableColumn<T>) => {
      e.preventDefault();
      if (menuAnchorEl) {
        handleMenuClose?.();
      }
      column.onCellContextMenu?.(e, item, column.id);
    },
    [menuAnchorEl, handleMenuClose],
  );

  return (
    <Box sx={containerStyle ?? {}}>
      {isLoading ? (
        <TableSkeleton height={'calc(100vh - 300px)'} />
      ) : (
        <>
          {isEmpty && !isInlineEmpty ? (
            placeholder
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', zIndex: 1 }}>
              <TableContainer
                component={Paper}
                data-table-empty={isInlineEmpty || undefined}
                sx={{
                  maxHeight,
                  overflow: 'auto',
                  width: '100%',
                  borderRadius: (theme) => `${theme.spacing(0.5)} ${theme.spacing(0.5)} 0 0`,
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((column, index) => {
                        return (
                          <TableCell
                            onClick={(e) => column?.onHeaderCellClick?.(e)}
                            sx={column.headerCellStyle}
                            key={index}
                            style={{ fontWeight: typography.fontWeightMedium, zIndex: 10 }}
                          >
                            {column.label}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, rowIndex) => {
                      const isActiveRow = activeRow === row;
                      const rowId = getRowId ? getRowId(row) : rowIndex;
                      const isExpanded = expandedRows?.has(rowId) ?? false;
                      const childRows = getChildRows?.(row);
                      const hasChildren = isRowExpandable?.(row) && childRows && childRows.length > 0;

                      return (
                        <React.Fragment key={rowId}>
                          {/* Parent Row */}
                          <TableRow
                            hover
                            data-has-menu={isActiveRow}
                            sx={{
                              backgroundColor: (th) => (isActiveRow ? th.palette.action.hover : 'inherit'),
                              '&:hover': {
                                backgroundColor: (th) => th.palette.action.hover,
                              },
                            }}
                          >
                            {columns.map((column, colIndex) => {
                              const cellStyle =
                                typeof column.bodyCellStyle === 'function'
                                  ? column.bodyCellStyle(row as T & Theme, false)
                                  : column.bodyCellStyle;

                              return (
                                <TableCell
                                  onContextMenu={(e) => onContextMenu(e, row, column)}
                                  onDoubleClick={() => column?.onBodyCellDoubleClick?.(row)}
                                  onClick={() => column?.onBodyCellClick?.(row)}
                                  sx={{
                                    ...cellStyle,
                                    '& .MuiTypography-root, & span': {
                                      fontSize: 14,
                                    },
                                  }}
                                  key={colIndex}
                                >
                                  {column.render(row, false)}
                                </TableCell>
                              );
                            })}
                          </TableRow>

                          {/* Child Rows */}
                          {hasChildren &&
                            isExpanded &&
                            childRows.map((childRow, childIndex) => {
                              const isActiveChildRow = activeRow === childRow;
                              return (
                                <TableRow
                                  key={`${rowIndex}-child-${childIndex}`}
                                  hover
                                  data-has-menu={isActiveChildRow}
                                  data-child-row="true"
                                  sx={{
                                    backgroundColor: (th) => (isActiveChildRow ? th.palette.action.hover : 'white'),
                                    '&:hover': {
                                      backgroundColor: (th) => th.palette.action.hover,
                                    },
                                  }}
                                >
                                  {columns.map((column, colIndex) => {
                                    const cellStyle =
                                      typeof column.bodyCellStyle === 'function'
                                        ? column.bodyCellStyle(childRow as T & Theme, true)
                                        : column.bodyCellStyle;

                                    return (
                                      <TableCell
                                        onContextMenu={(e) => onContextMenu(e, childRow, column)}
                                        onDoubleClick={() => column?.onBodyCellDoubleClick?.(childRow)}
                                        onClick={() => column?.onBodyCellClick?.(childRow)}
                                        sx={{
                                          ...cellStyle,
                                          ...(colIndex === 0 && { paddingLeft: 9 }),
                                          '& .MuiTypography-root, & span': {
                                            fontSize: 14,
                                          },
                                        }}
                                        key={colIndex}
                                      >
                                        {column.render(childRow, true)}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {isInlineEmpty ? (
                <Box
                  data-table-empty-content="true"
                  sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {emptyPlaceholderComponent}
                </Box>
              ) : (
                <Box
                  data-table-pagination="true"
                  sx={{
                    backgroundColor: 'white',
                    width: 'calc(100% + 2px)',
                    border: `1px solid ${palette.divider}`,
                    borderRadius: '0 0 4px 4px',
                    position: 'relative',
                    left: '-1px',
                  }}
                >
                  <TablePagination
                    component="div"
                    sx={{
                      '.MuiTablePagination-toolbar': {
                        minHeight: 44,
                      },
                      '.MuiTablePagination-actions .MuiIconButton-root': {
                        color: 'action.active',
                      },
                    }}
                    rowsPerPageOptions={perPageOptions}
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page - 1}
                    onPageChange={(_, newPage) => onPageChange(newPage + 1)}
                    onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
                    labelRowsPerPage="Items per page:"
                    slotProps={{
                      actions: {
                        previousButton: {
                          'data-action': 'pagination_prev_btn',
                        } as Partial<IconButtonProps>,
                        nextButton: {
                          'data-action': 'pagination_next_btn',
                        } as Partial<IconButtonProps>,
                      },
                      select: {
                        inputProps: {
                          'data-action': 'pagination_rows_per_page_select',
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
